import React, { useState } from 'react'
import i18n from 'constants/strings'
import styles from './watchlist-name-field.module.scss'
import formStyles from '../../generic/styles/forms.module.scss'
import cx from 'classnames'
import { MAX_NAME_LENGTH } from 'constants/watchlists'
import * as Yup from 'yup'
import { RequiredStringSchema } from 'yup/lib/string'

interface Props {
  label?: string
  value: string
  onValueChange: (value: string) => void
  error: null | string
}

interface Options {
  forbiddenNames?: string[]
  getValidationSchema?: (options: {
    forbiddenNames?: string[]
  }) => RequiredStringSchema<string | undefined, Record<string, any>>
}

export const useWatchlistNameField = (
  options?: Options
): {
  value: string
  setValue: React.Dispatch<React.SetStateAction<string>>
  validate: (value: string) => string | undefined
  clearError: () => void
  props: Omit<Props, 'label'>
} => {
  const { forbiddenNames } = options ?? {}
  const [value, setValue] = useState('')
  const [error, setError] = useState<null | string>(null)
  const defaultValidationSchema = Yup.string()
    .required(i18n.t('strCommonUI.form.validationErrors.watchlistNameRequired'))
    .matches(/^[a-z\d\-_\s]+$/i, i18n.t('strCommonUI.form.validationErrors.onlyAlphanumeric'))
    .test(
      'len',
      i18n.t('strCommonUI.form.validationErrors.maxChars', {
        name: i18n.t('strWatchlists.createModal.defaultLabel'),
        maxLength: MAX_NAME_LENGTH,
      }),
      val => {
        return val ? val.length <= MAX_NAME_LENGTH : false
      }
    )
    .notOneOf(forbiddenNames ?? [], i18n.t('strCommonUI.form.validationErrors.watchlistNameExists'))

  const validate = (value: string) => {
    try {
      defaultValidationSchema.validateSync(value)
      setError(null)
      return
    } catch (err) {
      const errorText = (err as Error)?.message
      setError(errorText)
      return errorText
    }
  }

  const handleValueChange = (value: string) => {
    setValue(value)
    if (value) {
      validate(value)
    } else {
      setError(null)
    }
  }

  return {
    value,
    setValue,
    validate,
    clearError: () => setError(null),
    props: {
      value,
      onValueChange: handleValueChange,
      error,
    },
  }
}

const WatchlistNameField = ({
  value,
  onValueChange,
  label = i18n.t('strWatchlists.createModal.defaultLabel'),
  error,
}: Props) => {
  return (
    <section>
      <label htmlFor='create-watchlist' className={formStyles.formLabel}>
        {label}
      </label>
      <input
        id='create-watchlist'
        type='text'
        className={cx(styles.commonTextInput, styles.createWatchlistField)}
        value={value}
        onChange={e => {
          if (e.target.value.length <= MAX_NAME_LENGTH) {
            onValueChange(e.target.value)
          }
        }}
      />
      {error ? (
        <p className={cx(styles.createWatchlistFieldInfo, styles.errorText)} role='alert'>
          {error}
        </p>
      ) : (
        <p className={styles.createWatchlistFieldInfo}>
          {i18n.t('strWatchlists.addOrManageModal.create.charsCount', {
            count: value.length,
            max: MAX_NAME_LENGTH,
          })}
        </p>
      )}
    </section>
  )
}

export default WatchlistNameField
